<template>
  <div class="job-summary-details">
    <div class="run-summary-meta">
      <div class="run-summary-meta-group">
        <span>Triggered via {{ run.triggerEvent || "-" }}</span>
        <template v-if="run.triggeredBy">
          <span class="run-summary-sep" aria-hidden="true">·</span>
          <span class="run-summary-by">by {{ run.triggeredBy }}</span>
        </template>
        <template v-if="run.triggeredAt">
          <span class="run-summary-sep" aria-hidden="true">·</span>
          <span :title="absoluteTime(run.triggeredAt)">{{ relativeTime(run.triggeredAt) }}</span>
        </template>
      </div>
      <div class="run-summary-meta-group">
        <ActionRunStatusIcon :status="run.status" class="mr-1" />
        <span class="run-summary-status">{{ run.status || "-" }}</span>
        <span class="run-summary-sep" aria-hidden="true">·</span>
        <span>Total Duration: {{ run.duration || "-" }}</span>
      </div>
    </div>

    <div v-if="run.runError" class="run-error-banner">
      <SvgIcon name="octicon-alert" class="mr-2" />
      {{ run.runError }}
    </div>

    <!-- Workflow dependency graph: jobs as nodes, `needs:` edges between them. -->
    <WorkflowGraph
      v-if="(run.jobs || []).length > 0"
      :jobs="run.jobs"
      :run-link="run.htmlurl"
      :trigger-event="run.triggerEvent"
    />

    <!-- Per-job step summaries. The backend renders each job's $GITHUB_STEP_SUMMARY
         to sanitized HTML and returns them in job order (matching GitHub); jobs
         without a summary are omitted server-side. -->
    <section v-if="jobSummaries.length" ref="summariesEl" class="run-job-summaries">
      <div
        v-if="run.canScrubSummaries && hasStoredSummaryContent"
        class="run-job-summaries-toolbar"
      >
        <button
          class="run-summaries-scrub"
          title="Remove all summaries for this run"
          @click="scrubSummaries"
        >
          <SvgIcon name="octicon-trashcan" class="mr-1" />
          Remove summaries
        </button>
      </div>
      <div
        class="run-job-summary"
        v-for="summary in jobSummaries"
        :key="summary.jobId"
      >
        <div class="run-job-summary-title">
          {{ summary.jobName || `Job ${summary.jobId}` }}
        </div>
        <!-- Annotations for content that was dropped (over the cap) or scrubbed. -->
        <div
          v-for="notice in summary.notices || []"
          :key="notice"
          class="run-job-summary-notice"
        >
          <SvgIcon name="octicon-alert" class="mr-2" />
          {{ noticeText(notice) }}
        </div>
        <!-- summaryHTML is sanitized server-side by the fork's UGC markdown pipeline;
             .markdown is the global class that carries GFM styling into v-html content. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-if="summary.summaryHTML" class="markdown" v-html="summary.summaryHTML" />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, toRefs, watch } from "vue";
import { SvgIcon } from "../svg.js";
import { renderMermaid } from "../markdown/mermaid.js";
import ActionRunStatusIcon from "./ActionRunStatusIcon.vue";
import WorkflowGraph from "./WorkflowGraph.vue";

// Props
const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
});

const { csrf: csrfToken } = window.config;

const { currentRun: run } = toRefs(props.store.viewData);

const jobSummaries = computed(() => run.value.jobSummaries || []);

const summariesEl = ref(null);

// Mermaid fences arrive via v-html after the page-load markdown pass has already
// run (the fork has no upstream-style global .markup observer), so render them
// here whenever summary content lands in the DOM. renderMermaid replaces each
// <pre> with the diagram, so re-runs on poll/scrub reloads are no-ops.
const renderSummaryMermaid = () => {
  if (!summariesEl.value) return;
  renderMermaid(
    summariesEl.value.querySelectorAll("code.language-mermaid"),
  ).catch((error) => console.error("Error rendering mermaid diagrams:", error));
};

watch(jobSummaries, renderSummaryMermaid, { flush: "post" });

// Hide the scrub button once nothing renders as content (e.g. everything is
// already tombstoned): there is nothing left to remove.
const hasStoredSummaryContent = computed(() =>
  jobSummaries.value.some((s) => s.summaryHTML),
);

// Notice tokens are stable enums from the backend; display text lives here,
// matching the component's hardcoded-English convention.
const noticeText = (notice) => {
  switch (notice) {
    case "dropped-oversize":
      return "A step summary was not stored: it would exceed the job's 1 MiB summary limit.";
    case "scrubbed":
      return "Summary content was removed by a repository administrator.";
    default:
      return notice;
  }
};

const scrubSummaries = async () => {
  if (
    !window.confirm("Remove all summaries for this run? This cannot be undone.")
  )
    return;
  try {
    await window.fetch(`${run.value.htmlurl}/summaries/scrub`, {
      method: "POST",
      headers: {
        "X-Csrf-Token": csrfToken,
      },
    });
    await props.store.forceReloadCurrentRun();
  } catch (error) {
    console.error("Error scrubbing summaries:", error);
  }
};

const absoluteTime = (ts) => new Date(ts * 1000).toLocaleString();

// Relative "when" for the run-summary header, matching the Figma handoff.
const relativeTime = (ts) => {
  const diff = ts - Date.now() / 1000;
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return rtf.format(Math.round(diff), "second");
};

// Lifecycle: the Summary view owns the run-level polling
onMounted(() => {
  props.store.startPollingCurrentRun();
  // covers remounts (job view -> summary view) where the store is already loaded
  renderSummaryMermaid();
});

onBeforeUnmount(() => {
  props.store.stopPollingCurrentRun();
});
</script>

<style scoped lang="less">
.run-error-banner {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 8px 12px;
  margin: 10px 10px 0;
  background-color: var(--copia-red-02);
  color: var(--copia-red-01);
  border: 1px solid var(--copia-red-01);
  border-radius: 4px;
  font-size: 13px;
}

.job-summary-details {
  display: flex;
  flex-direction: column;
  flex: 1;

  // Compact one-line run header (Figma handoff): trigger/user/time on the left,
  // status + total duration on the right, sharing the grey title band.
  .run-summary-meta {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 4px 16px;
    padding: 8px 12px;
    font-size: 12px;
    color: var(--copia-grey-04);
    background-color: var(--copia-grey-01);
    border-bottom: 1px solid var(--copia-grey-02);
  }

  .run-summary-meta-group {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .run-summary-status {
    text-transform: capitalize;
  }

  .run-summary-sep {
    color: var(--copia-grey-03);
  }

  // No inner scroll: the page owns vertical scrolling, and an inner scrollbar
  // would eat gutter width and knock the cards' right edge out of line with
  // the graph panel above.
  .run-job-summaries {
    padding: 0 10px 10px;
  }

  .run-job-summaries-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .run-summaries-scrub {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    padding: 0;
    font-size: 12px;
    color: var(--copia-grey-04);
    cursor: pointer;

    &:hover {
      color: var(--copia-red-01);
    }
  }

  .run-job-summary {
    margin-top: 10px;
    border: 1px solid var(--copia-grey-02);
    border-radius: 8px;
    overflow: hidden;
    background-color: var(--copia-white);

    .run-job-summary-title {
      padding: 8px 12px;
      font-size: 12px;
      color: var(--copia-grey-04);
      background-color: var(--copia-grey-01);
    }

    .run-job-summary-notice {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      font-size: 13px;
      font-style: italic;
      color: var(--copia-grey-04);
    }

    // .markdown carries GFM styling; keep the rendered summary padded inside the card
    .markdown {
      padding: 8px 12px 12px;
      font-size: 14px;
      // summaries are arbitrary user content: wrap unbreakable runs instead of
      // letting them widen the card past the panel
      overflow-wrap: anywhere;
    }
  }
}
</style>
